import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Button, FormWrapper, ImageUploadModal, SubmitButton, ViewMediaModal } from "../../components";

function NewBankForm({
  disableInputs = false,
  handleSave,
  showCancel,
  handleCancel,
  editable,
  data,
  parentName,
  id,
  formId,
}) {
  const [formData, setFormData] = useState({
    name_in_bank: "",
    bank_name: "",
    bank_account_type: "",
    bank_account_number: "",
    bank_ifsc: "",
    bank_branch: "",
    bank_identification: "",
    bank_identification_url: "",
  });
  const [editedData, setEditedData] = useState({});

  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [showViewMediaModal, setShowViewMediaModal] = useState(false);

  useEffect(() => {
    if (editable && data) {
      Object.keys(formData).forEach((key) => {
        setFormData((prevFormData) => ({ ...prevFormData, [key]: data[key] }));
      });
    }
  }, []);

  const handleFormInputChange = (e, useTextCase) => {
    const { name, value } = e.target;
    let newValue = value;
    if (useTextCase === "capitalize")
      newValue = value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));
    if (useTextCase === "uppercase") newValue = value.toUpperCase();

    setFormData((prevFormData) => ({ ...prevFormData, [name]: newValue }));
    setEditedData((prevData) => ({ ...prevData, [name]: value }));
    const savedFormData = JSON.parse(localStorage.getItem("new_bank_data"));
    localStorage.setItem("new_bank_data", JSON.stringify({ ...savedFormData, [name]: value }));
    if (formId && id) {
      const savedFormData = JSON.parse(localStorage.getItem(formId));
      if (!savedFormData) localStorage.setItem(formId, JSON.stringify({ [id]: { [name]: value } }));
      else if (!savedFormData[id])
        localStorage.setItem(formId, JSON.stringify({ ...savedFormData, [id]: { [name]: value } }));
      else
        localStorage.setItem(
          formId,
          JSON.stringify({ ...savedFormData, [id]: { ...savedFormData[id], [name]: value } })
        );
    }
  };

  const handleRadioChange = (value) => {
    setFormData((prevFormData) => ({ ...prevFormData, bank_account_type: value }));
    setEditedData((prevData) => ({ ...prevData, bank_account_type: value }));
    const savedData = JSON.parse(localStorage.getItem("new_bank_data"));
    localStorage.setItem("new_bank_data", JSON.stringify({ ...savedData, bank_account_type: value }));
    if (formId && id) {
      const savedFormData = JSON.parse(localStorage.getItem(formId));
      if (!savedFormData) localStorage.setItem(formId, JSON.stringify({ [id]: { bank_account_type: value } }));
      else if (!savedFormData[id])
        localStorage.setItem(formId, JSON.stringify({ ...savedFormData, [id]: { bank_account_type: value } }));
      else
        localStorage.setItem(
          formId,
          JSON.stringify({ ...savedFormData, [id]: { ...savedFormData[id], bank_account_type: value } })
        );
    }
  };

  const handleImageUpload = (imageId, url) => {
    setShowImageUploadModal(false);
    setFormData((prevFormData) => ({ ...prevFormData, bank_identification: imageId, bank_identification_url: url }));
    setEditedData((prevData) => ({ ...prevData, bank_identification: imageId, bank_identification_url: url }));
    const savedData = JSON.parse(localStorage.getItem("new_bank_data"));
    localStorage.setItem(
      "new_bank_data",
      JSON.stringify({ ...savedData, bank_identification: imageId, bank_identification_url: url })
    );
    if (formId && id) {
      const savedFormData = JSON.parse(localStorage.getItem(formId));
      if (!savedFormData) {
        localStorage.setItem(
          formId,
          JSON.stringify({ [id]: { bank_identification: imageId, bank_identification_url: url } })
        );
      } else if (!savedFormData[id]) {
        localStorage.setItem(
          formId,
          JSON.stringify({ ...savedFormData, [id]: { bank_identification: imageId, bank_identification_url: url } })
        );
      } else {
        localStorage.setItem(
          formId,
          JSON.stringify({
            ...savedFormData,
            [id]: { ...savedFormData[id], bank_identification: imageId, bank_identification_url: url },
          })
        );
      }
    }
  };

  const onCancel = () => {
    localStorage.removeItem("new_bank_data");
    handleCancel();
  };

  const validateForm = () => {
    let isValid = true;
    Object.keys(formData).forEach((key) => {
      if (!formData[key] && key !== "bank_identification") {
        if (key === "bank_identification_url") {
          toast.error("Please enter a valid passbook/canceled cheque photo");
        } else {
          toast.error(`Please enter a valid ${key}`);
        }
        isValid = false;
        return false;
      }
    });
    return isValid;
  };

  const handleSaveBank = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    else {
      if (editable && data) {
        await handleSave(editedData);
        onCancel();
      } else {
        await handleSave(formData);
      }
    }
  };

  return (
    <>
      <FormWrapper id={id ? `${id}_form` : "new_bank_form"} onSubmit={handleSaveBank}>
        <section className="section grid_3">
          <section className="column">
            <input
              type="text"
              required
              name="name_in_bank"
              value={formData.name_in_bank}
              onChange={(e) => handleFormInputChange(e, "capitalize")}
              className="input input_round"
              placeholder={`Enter ${parentName ? parentName.toLowerCase() : ""} name (as per bank)`}
              disabled={disableInputs}
              maxLength={50}
              pattern="([a-zA-Z\s+]+)"
              title="Only alphabets are allowed"
            />
            <input
              type="text"
              required
              name="bank_name"
              value={formData.bank_name}
              onChange={(e) => handleFormInputChange(e, "capitalize")}
              className="input input_round"
              placeholder="Enter bank name"
              disabled={disableInputs}
              maxLength={50}
              pattern="([a-zA-Z\s+]+)"
              title="Only alphabets are allowed"
            />
            <input
              type="text"
              required
              name="bank_account_number"
              value={formData.bank_account_number}
              maxLength={20}
              onChange={handleFormInputChange}
              className="input input_round"
              placeholder="Account number"
              disabled={disableInputs}
              pattern="[0-9]+"
              title="Only numbers are allowed"
            />
            <div className="row">
              <span className="label">Type of Account</span>
              <div className="row">
                <label htmlFor="current" className="label radio_label">
                  <input
                    id="current"
                    type="radio"
                    required
                    name={`${id}_account_type`}
                    value="current"
                    checked={formData.bank_account_type?.toLowerCase() === "current"}
                    onChange={() => handleRadioChange("Current")}
                    disabled={disableInputs}
                  />
                  Current
                </label>
                <label htmlFor="savings" className="label radio_label">
                  <input
                    id="savings"
                    type="radio"
                    name={`${id}_account_type`}
                    required
                    value="savings"
                    onChange={() => handleRadioChange("Savings")}
                    checked={formData.bank_account_type?.toLowerCase() === "savings"}
                    disabled={disableInputs}
                  />
                  Savings
                </label>
              </div>
            </div>
          </section>
          <div className="vertical_divider"></div>
          <section className="column">
            <input
              type="text"
              required
              name="bank_ifsc"
              value={formData.bank_ifsc}
              onChange={(e) => handleFormInputChange(e, "uppercase")}
              className="input input_round"
              placeholder="Enter IFSC code"
              disabled={disableInputs}
              maxLength={11}
              pattern="^[A-Z0-9]+$"
              title="Only alphabets and numbers are allowed"
            />
            <input
              type="text"
              required
              name="bank_branch"
              value={formData.bank_branch}
              onChange={(e) => handleFormInputChange(e, "capitalize")}
              className="input input_round"
              placeholder="Enter bank branch"
              disabled={disableInputs}
              maxLength={50}
              pattern="([a-zA-Z\s+]+)"
              title="Only alphabets are allowed"
            />
            <div className="row">
              <span className="label">Canceled cheque / Passbook photo</span>
              {formData?.bank_identification_url ? (
                <Button className="ghost" onClick={() => setShowViewMediaModal(true)}>
                  preview
                </Button>
              ) : (
                <span className=" gray small">No file uploaded</span>
              )}
              <Button className="upload" disabled={disableInputs} onClick={() => setShowImageUploadModal(true)}>
                Upload
              </Button>
            </div>
          </section>
        </section>
        <div className="row end">
          {showCancel && Object.keys(data).length > 0 && (
            <Button className="ghost_gray" onClick={onCancel} disabled={disableInputs}>
              Cancel
            </Button>
          )}
          <SubmitButton form={id ? `${id}_form` : "new_bank_form"} className="submit" disabled={disableInputs}>
            Save
          </SubmitButton>
        </div>
      </FormWrapper>

      {showImageUploadModal &&
        createPortal(
          <ImageUploadModal
            title="Upload canceled cheque/Passbook photo"
            handleClose={() => setShowImageUploadModal(false)}
            isVisible={showImageUploadModal}
            handleUpload={handleImageUpload}
          />,
          document.body
        )}

      {showViewMediaModal &&
        createPortal(
          <ViewMediaModal
            title="Canceled cheque/Passbook photo"
            isVisible={showViewMediaModal}
            handleClose={() => setShowViewMediaModal(false)}
            url={[formData?.bank_identification_url]}
            mediaType="image"
          />,
          document.body
        )}
    </>
  );
}

export default NewBankForm;
